import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

export type Toast = {
  id: number;
  title?: string;
  message: string;
};

type ToastCtx = {
  addToast: (t: Omit<Toast, "id">) => void;
};

const Ctx = createContext<ToastCtx | null>(null);

let counter = 0;
const TTL = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    timers.current.delete(id);
  }, []);

  const addToast = useCallback(
    (t: Omit<Toast, "id">) => {
      const id = ++counter;
      setToasts((prev) => [...prev.slice(-4), { ...t, id }]);
      timers.current.set(id, setTimeout(() => remove(id), TTL));
    },
    [remove],
  );

  return (
    <Ctx.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg"
          >
            <div className="min-w-0 flex-1">
              {t.title && (
                <p className="text-sm font-semibold text-slate-900 truncate">{t.title}</p>
              )}
              <p className="text-sm text-slate-600 truncate">{t.message}</p>
            </div>
            <button
              onClick={() => remove(t.id)}
              className="shrink-0 text-slate-400 hover:text-slate-600"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx.addToast;
}
